import React from 'react'

import {
  BriefcaseIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  HomeIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import { BriefcaseIcon as BriefcaseIconSolid } from '@heroicons/react/24/solid'
import { Form, Formik } from 'formik'
import ReactTooltip from 'react-tooltip'
import * as Yup from 'yup'

import CallPhone from '@/components/CallPhone'
import { InputField } from '@/components/FormikInputs'
import useCardsCollapseStatus from '@/features/FileContacts/useCardsCollapseStatus'
import {
  useCreateApplicationPartyMutation,
  useUpdateApplicationPartyMutation,
} from '@/services/v2-application/applicationParties'
import { Application, ApplicationParty, ApplicationPartyType } from '@/types/unlock'
import { shallowObjectCompare } from '@/utils'

interface ContactCardProps {
  applicationParty: ApplicationParty
  application: Application
  type: {
    name: string
    type: ApplicationPartyType
    category: 'user' | 'company' | 'applicant' | 'other'
    predefined?: boolean
  }
  index: number | string
  handleDeleteContact: (string) => void
  handleSendEmail: (recipient: string) => void
  shouldBeLocked: boolean
}

const isHOA = (contact_type) => contact_type === 'hoa'

const validationSchema = Yup.object().shape({
  title: Yup.string().when('contact_type', {
    is: isHOA,
    then: (schema) => schema.required('Title is required'),
  }),
  hoa_subdivision_name: Yup.string(),
})

export const ContactCard = (props: ContactCardProps) => {
  const { isExpanded, toggleIsExpanded } = useCardsCollapseStatus(false)
  const [updatePost] = useUpdateApplicationPartyMutation()
  const [createPost] = useCreateApplicationPartyMutation()

  const {
    applicationParty,
    application,
    type,
    handleDeleteContact,
    handleSendEmail,
    shouldBeLocked,
  } = props

  const initialValues = !applicationParty?.party?.deleted_at
    ? {
        contact_name: applicationParty?.party?.contact_name || '',
        name: applicationParty?.party?.name || '',
        phone: applicationParty?.party?.phone || '',
        email: applicationParty?.party?.email || '',
        address: applicationParty?.party?.address || '',
        title: applicationParty?.party?.title || '',
        hoa_subdivision_name: applicationParty?.party?.hoa_subdivision_name || '',
        contact_type: type.type,
      }
    : {}

  const handleSubmit = (values) => {
    if (!shallowObjectCompare(initialValues, values)) {
      if (applicationParty) {
        updatePost({
          applicationUUID: application.uuid,
          applicationPartyUUID: applicationParty.uuid,
          party: {
            uuid: applicationParty.party.uuid,
            ...values,
          },
        })
      } else {
        createPost({
          applicationUUID: application.uuid,
          type: type.type,
          party: {
            ...values,
          },
        })
      }
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ submitForm, values }) => (
        <Form>
          <div className='w-full card bg-base-100 shadow-md flex flex-col border'>
            <div
              onClick={() => toggleIsExpanded()}
              className={`w-full flex justify-between px-5 py-2 ${
                isExpanded && 'bg-[#24d3ee]'
              } transition-colors duration-500 rounded-t-box  cursor-pointer`}
            >
              <div className='flex flex-col text-start'>
                <p className='text-base font-bold'>{type?.name}</p>
              </div>
              <div className='flex items-center gap-2'>
                {!isExpanded && (
                  <>
                    <div
                      className='tooltip tooltip-left'
                      data-tip={`Phone: ${values.phone}`}
                    >
                      <CallPhone callTo={values.phone} />
                    </div>
                    {window && (
                      <div
                        className='tooltip tooltip-left'
                        data-tip={`E-mail: ${values.email}`}
                      >
                        <EnvelopeIcon
                          className='w-4 cursor-pointer'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSendEmail(values?.email)
                          }}
                        />
                      </div>
                    )}
                    {!type?.predefined && !shouldBeLocked && (
                      <TrashIcon
                        className='w-4 hover:text-error cursor-pointer transition-all'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteContact(applicationParty?.uuid)
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <div className={`px-5 pb-2 pt-0 flex flex-col ${isExpanded && 'pt-2'}`}>
              <div className='flex flex-row gap-1 text-xs'>
                {type.category === 'company' && (
                  <>
                    <BuildingOfficeIcon className='w-4' />
                    <InputField
                      readOnly={!isExpanded || shouldBeLocked}
                      name='name'
                      onBlur={submitForm}
                      placeholder={type.type === 'hoa' ? 'HOA Name' : 'Company name'}
                      parentClassName='w-full'
                    />
                  </>
                )}
              </div>
              {type.type === 'hoa' && (
                <>
                  <div className='flex flex-row gap-1 text-xs'>
                    <BriefcaseIconSolid className='w-4' />
                    <InputField
                      disabled={!isExpanded || shouldBeLocked}
                      name='hoa_subdivision_name'
                      onBlur={submitForm}
                      placeholder='Neighborhood/Building Name'
                      parentClassName='w-full'
                      showError
                    />
                  </div>
                </>
              )}
              {((!isExpanded && type.category !== 'company') || isExpanded) && (
                <div className='flex flex-row gap-1 text-xs'>
                  <UserCircleIcon className='w-4' />
                  <InputField
                    disabled={!isExpanded || shouldBeLocked}
                    name='contact_name'
                    onBlur={submitForm}
                    placeholder={
                      values.contact_type === 'hoa' ? 'HOA Contact Name*' : 'Contact Name'
                    }
                    parentClassName='w-full'
                  />
                </div>
              )}
              {isExpanded && (
                <>
                  {type.type === 'hoa' && (
                    <>
                      <div className='flex flex-row gap-1 text-xs'>
                        <BriefcaseIcon className='w-4' />
                        <InputField
                          disabled={!isExpanded || shouldBeLocked}
                          name='title'
                          onBlur={submitForm}
                          placeholder='Title'
                          parentClassName='w-full'
                          showError
                        />
                      </div>
                    </>
                  )}
                  <div className='flex flex-row gap-1 text-xs items-center'>
                    <CallPhone callTo={values.phone} />
                    <InputField
                      name='phone'
                      onBlur={submitForm}
                      placeholder='Phone'
                      parentClassName='w-full'
                      disabled={shouldBeLocked}
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs'>
                    <EnvelopeIcon
                      className='w-4 cursor-pointer'
                      onClick={() => handleSendEmail(values?.email)}
                    />
                    <InputField
                      name='email'
                      onBlur={submitForm}
                      placeholder='E-mail'
                      parentClassName='w-full'
                      disabled={shouldBeLocked}
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs'>
                    <HomeIcon className='w-4' />
                    {applicationParty?.has_shared_address_with_primary_applicant && (
                      <ReactTooltip
                        id='address-input'
                        place='left'
                      >
                        This address is being shared with the Primary Applicant, please recreate
                        this Contact if you need update it.
                      </ReactTooltip>
                    )}
                    <InputField
                      name='address'
                      onBlur={submitForm}
                      placeholder='Address'
                      parentClassName='w-full'
                      disabled={
                        applicationParty?.has_shared_address_with_primary_applicant ||
                        shouldBeLocked
                      }
                    />
                  </div>
                  {!type?.predefined && !shouldBeLocked && (
                    <div className='flex justify-end mr-1 mt-2'>
                      <TrashIcon
                        className='w-5 hover:text-error cursor-pointer transition-all'
                        onClick={() => handleDeleteContact(applicationParty?.uuid)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
